import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Typography, Card, Row, Col, Statistic, Spin, Alert, List, Empty, Button } from 'antd'; // Added Button
import { ScheduleOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons'; // Example icons
import { useAuth } from '../context/AuthContext'; // To get current user
import { Link } from 'react-router-dom'; // To link to patient details in title
import { usePatientChart } from '../context/PatientChartContext'; // Import the context hook
import WorkBasketWidget from '../components/WorkBasketWidget'; // Import work basket widget

const { Title, Text, Paragraph } = Typography; // Add Paragraph

// Helper to format time
const formatTime = (dateString) => {
    return dateString ? moment(dateString).format('HH:mm A') : 'N/A';
};

const DashboardPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [totalAppointmentsCount, setTotalAppointmentsCount] = useState(0); // State for total appointments count
  const [patientCount, setPatientCount] = useState(0); // State for patient count
  const [recentPatients, setRecentPatients] = useState([]); // State for recent patients
  const [loadingAppointments, setLoadingAppointments] = useState(true); // Separate loading states
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [error, setError] = useState(null); // Combined error state for simplicity
  const { user } = useAuth(); // Get logged-in user info
  const { addChart, showChart } = usePatientChart(); // Get addChart and showChart functions from context

  const fetchTodaysAppointments = useCallback(async () => {
    setLoadingAppointments(true); // Use specific loading state
    // setError(null); // Clear error at the start of all fetches in useEffect
    try {
      const today = moment().format('YYYY-MM-DD');
      const params = {
        startDate: today,
        endDate: today,
      };
      // If the logged-in user is a provider (e.g., MD, Nursing), fetch only their appointments
      // Adjust roles as needed
      if (user && ['MD', 'Nursing'].includes(user.role)) {
        params.provider = user._id;
      }
      // Admins/Schedulers might see all appointments for today

      const response = await axios.get('/appointments', { params }); // Removed prefix
      setAppointments(response.data.data.appointments);
    } catch (err) {
      console.error("Error fetching today's appointments:", err);
      setError(err.response?.data?.message || "Failed to fetch today's appointments."); // Use combined error state
    } finally {
      setLoadingAppointments(false); // Use specific loading state
    }
  }, [user]); // Re-fetch if user changes

  // Fetch total appointments count for today (system-wide)
  const fetchTotalAppointmentsCount = useCallback(async () => {
    try {
      const today = moment().format('YYYY-MM-DD');
      const params = {
        startDate: today,
        endDate: today,
      };
      // Don't filter by provider - we want all appointments for the system
      const response = await axios.get('/appointments', { params });
      setTotalAppointmentsCount(response.data.data.appointments.length);
    } catch (err) {
      console.error("Error fetching total appointments count:", err);
      // Don't set error as this is for statistics only
    }
  }, []);

  // Fetch total patient count
  const fetchPatientCount = useCallback(async () => {
     setLoadingStats(true); // Use specific loading state
     try {
       // Fetching with limit=0 might just return count in some APIs,
       // or fetch 1 to get the 'results' field from our backend structure
       const response = await axios.get('/patients?limit=1'); // Removed prefix
       setPatientCount(response.data.results || 0); // Use the 'results' field from the response
     } catch (err) {
       console.error("Error fetching patient count:", err);
       // Handle error separately or ignore for dashboard count display
     } finally {
       setLoadingStats(false); // Use specific loading state
     }
  }, []);

  // Fetch recent patients (based on user access history)
  const fetchRecentPatients = useCallback(async () => {
    setLoadingRecent(true);
    try {
      // Use the new recent patients endpoint that tracks user access
      const response = await axios.get('/patients/recent?limit=5');
      setRecentPatients(response.data.data.patients);
    } catch (err) {
      console.error("Error fetching recent patients:", err);
      // Use combined error state, maybe less critical than appointments
      // setError(err.response?.data?.message || "Failed to fetch recent patients.");
    } finally {
      setLoadingRecent(false);
    }
  }, []);


  useEffect(() => {
    setError(null); // Clear errors on initial load/refresh
    fetchTodaysAppointments();
    fetchTotalAppointmentsCount(); // Fetch total appointments count for statistics
    fetchPatientCount();
    fetchRecentPatients(); // Fetch recent patients
  }, [fetchTodaysAppointments, fetchTotalAppointmentsCount, fetchPatientCount, fetchRecentPatients]); // Add new dependency

  return (
    <div>
      <Title level={2}>Dashboard</Title>
      <Paragraph>Welcome back, {user?.name || 'User'}!</Paragraph>

      {/* Example Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic title="Today's Appointments" value={totalAppointmentsCount} prefix={<ScheduleOutlined />} />
          </Card>
        </Col>
        {/* Add more stats later (e.g., total patients, pending tasks) */}
         <Col xs={24} sm={12} md={8}>
           <Card>
             <Statistic title="Total Patients" value={patientCount} prefix={<TeamOutlined />} />
           </Card>
         </Col>
      </Row>

      {/* Dashboard Content Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {/* Today's Appointments */}
        <Col xs={24} lg={12}>
          <Card title={<><ScheduleOutlined /> Today's Appointments</>}>
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />} {/* Show combined error here */}
        <Spin spinning={loadingAppointments}> {/* Use specific loading state */}
          <List
            itemLayout="horizontal"
            dataSource={appointments}
            renderItem={item => (
              <List.Item
                 actions={item.patient?._id ? [
                   <Button
                     type="link"
                     onClick={() => {
                       addChart(item.patient);
                       showChart();
                     }}
                   >
                     View Patient
                   </Button>
                 ] : []}
              >
                <List.Item.Meta
                  title={`${formatTime(item.startTime)} - ${formatTime(item.endTime)}: ${item.patient?.firstName || ''} ${item.patient?.lastName || 'N/A'}`}
                  description={`Type: ${item.appointmentType} | Provider: ${item.provider?.name || 'N/A'} | Status: ${item.status}`}
                />
              </List.Item>
            )}
             locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No appointments scheduled for today" /> }}
          />
        </Spin>
          </Card>
        </Col>

        {/* Work Basket Widget */}
        <Col xs={24} lg={12}>
          <WorkBasketWidget />
        </Col>
      </Row>

      {/* Recent Patients List */}
      <Card title={<><UserOutlined /> Recent Patients</>} style={{ marginTop: 24 }}>
         {/* Maybe show a different error or loading state? For now, reuse main ones */}
         {/* {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />} */}
         <Spin spinning={loadingRecent}>
           <List
             itemLayout="horizontal"
             dataSource={recentPatients}
             renderItem={item => (
               <List.Item
                  // Ensure item._id is valid before creating link
                  // Replace Link with Button using addChart context function
                  actions={item?._id ? [<Button type="link" onClick={() => addChart(item)}>Open Chart</Button>] : []}
               >
                 <List.Item.Meta
                   // Make patient name clickable using addChart context function
                   title={item?._id ?
                     <Button
                       type="link"
                       style={{ padding: 0, height: 'auto', fontSize: 'inherit', fontWeight: 'inherit' }}
                       onClick={() => {
                         addChart(item);
                         showChart();
                       }}
                     >
                       {`${item.lastName}${item.middleName ? ' ' + item.middleName : ''}, ${item.firstName}`}
                     </Button> :
                     `${item.lastName}${item.middleName ? ' ' + item.middleName : ''}, ${item.firstName}`
                   }
                   description={`MRN: ${item.mrn || 'N/A'} | DOB: ${item.dateOfBirth ? moment(item.dateOfBirth).format('DD/MM/YYYY') : 'N/A'}`}
                 />
               </List.Item>
             )}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent patient activity" /> }}
           />
         </Spin>
       </Card>

    </div>
  );
};

export default DashboardPage;
